import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _PropTypes from 'prop-types';
import { rgba } from 'polished';
import * as React from 'react';
import { createElement } from 'react';
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd';
import { HTML5Backend, getEmptyImage } from 'react-dnd-html5-backend';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { getDropShadowByElevation } from '../../styles/elevation.js';
import { getSpacing, spacing1x } from '../../styles/spacing.js';
import { getTree } from '../../utils/tree.js';
import { Button } from '../Button/Button.js';
import { Checkbox } from '../Checkbox/Checkbox.js';
import { IconArrowDown } from '../Icons/IconArrowDown.js';
import { IconArrowRight } from '../Icons/IconArrowRight.js';
import { IconDrag } from '../Icons/IconDrag.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { Descender } from './Descender.js';
import { TreeBase } from './TreeBase.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

var dnd = {
  useDrag: useDrag,
  useDragLayer: useDragLayer,
  useDrop: useDrop
};
var lineHeight = getSpacing(8);
var lineHeightValue = getSpacingValue(getSpacing(8));
var horizontalSeparation = getSpacingValue(getSpacing(2));
var defaultBorderWidth = 2;
var StyledIconWrapper = styled.div.withConfig({
  displayName: "Tree__StyledIconWrapper",
  componentId: "sc-1178m10-0"
})(function () {
  return {
    lineHeight: lineHeight,
    marginLeft: horizontalSeparation
  };
});
var StyledLabel = styled.div.withConfig({
  displayName: "Tree__StyledLabel",
  componentId: "sc-1178m10-1"
})(function (_ref) {
  var isDragging = _ref.isDragging;
  return {
    cursor: "default",
    flex: 1,
    lineHeight: lineHeight,
    marginLeft: horizontalSeparation,
    overflow: "hidden",
    textOverflow: "ellipsis",
    userSelect: "none",
    visibility: isDragging ? "hidden" : void 0,
    whiteSpace: "nowrap"
  };
});
var StyledDragHandle = styled.div.withConfig({
  displayName: "Tree__StyledDragHandle",
  componentId: "sc-1178m10-2"
})(function (_ref2) {
  var _ref2$borderWidth = _ref2.borderWidth,
      borderWidth = _ref2$borderWidth === void 0 ? defaultBorderWidth : _ref2$borderWidth,
      disabled = _ref2.disabled,
      dragging = _ref2.dragging,
      isDragPlaceholder = _ref2.isDragPlaceholder,
      theme = _ref2.theme;
  return _objectSpread(_objectSpread({}, function () {
    if (isDragPlaceholder) {
      return {
        opacity: 0
      };
    } else if (dragging) {
      return {
        backgroundColor: theme.CTAColors.primaryVariant,
        color: theme.neutralColors.white
      };
    } else {
      return {
        backgroundColor: theme.neutralColors.silver
      };
    }
  }()), {}, {
    borderRadius: 2,
    cursor: !disabled ? "move" : "not-allowed",
    lineHeight: "".concat(lineHeightValue + borderWidth * 2, "px"),
    margin: -borderWidth,
    textAlign: "center",
    width: getSpacing(6)
  });
});

var Tree = function Tree(_ref3) {
  var canOrder = _ref3.canOrder,
      _ref3$canDrag = _ref3.canDrag,
      canDrag = _ref3$canDrag === void 0 ? function () {
    return true;
  } : _ref3$canDrag,
      _ref3$canDrop = _ref3.canDrop,
      canDrop = _ref3$canDrop === void 0 ? function () {
    return true;
  } : _ref3$canDrop,
      filterBy = _ref3.filterBy,
      getInitialHiddenIds = _ref3.getInitialHiddenIds,
      nodeById = _ref3.nodeById,
      onChange = _ref3.onChange,
      _onCollapse = _ref3.onCollapse,
      _onExpand = _ref3.onExpand,
      orderBy = _ref3.orderBy,
      renderButtons = _ref3.renderButtons,
      renderIcon = _ref3.renderIcon,
      renderLabel = _ref3.renderLabel,
      variant = _ref3.variant;

  var _React$useState = React.useState(function () {
    return getInitialHiddenIds ? getInitialHiddenIds(getTree({
      filterBy: filterBy,
      nodeById: nodeById,
      orderBy: orderBy
    }).context) : new Set();
  }),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      hiddenIds = _React$useState2[0],
      setHiddenIds = _React$useState2[1];

  var _React$useState3 = React.useState(),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      a11yDragId = _React$useState4[0],
      setA11yDragId = _React$useState4[1];

  return /*#__PURE__*/jsxs(DndProvider, {
    backend: HTML5Backend,
    children: [/*#__PURE__*/jsx(DragNode, {
      renderLabel: renderLabel
    }), /*#__PURE__*/jsx(TreeBase, {
      "aria-multiselectable": (variant === null || variant === void 0 ? void 0 : variant.type) === "multiselect",
      filterBy: filterBy,
      getAriaDropEffect: onChange && a11yDragId ? function (options) {
        return canDropNodeValid({
          canDrop: canDrop,
          source: {
            context: options.context.treeContext.getNodeContext(a11yDragId),
            node: nodeById[a11yDragId]
          },
          target: options
        }) ? "move" : void 0;
      } : void 0,
      getAriaGrabbed: onChange ? function (props) {
        if (a11yDragId === props.context.id) {
          return true;
        } else if (canDrag(props)) {
          return "supported";
        } else {
          return false;
        }
      } : void 0,
      getAriaSelected: function getAriaSelected(options) {
        if ((variant === null || variant === void 0 ? void 0 : variant.type) === "multiselect") {
          return !!variant.renderCheckbox(options).checked;
        } else if ((variant === null || variant === void 0 ? void 0 : variant.type) === "select" && variant.isSelected(options)) {
          return true;
        }
      },
      hideBy: function hideBy(_ref4) {
        var context = _ref4.context;
        return hiddenIds.has(context.id);
      },
      nodeById: nodeById,
      onChangeHiddenIds: setHiddenIds,
      onDefaultAction: function onDefaultAction(options) {
        switch (variant === null || variant === void 0 ? void 0 : variant.type) {
          case "multiselect":
            variant.onDefaultAction(options);
            break;

          case "select":
            variant.onSelect(options);
            break;
        }

        if (onChange && options.event.key === KEYS.SPACE) {
          if (!a11yDragId) {
            if (canDrag(options)) {
              setA11yDragId(options.context.id);
            }
          } else {
            var source = {
              context: options.context.treeContext.getNodeContext(a11yDragId),
              node: nodeById[a11yDragId]
            };

            if (canDropNodeValid({
              canDrop: canDrop,
              source: source,
              target: options
            })) {
              onChange({
                position: "childof",
                source: source,
                target: options
              });
              var nextHiddenIds = new Set(hiddenIds);

              if (options.context.isExpanded() || !options.context.hasChildren()) {
                nextHiddenIds.delete(source.context.id);
              } else {
                nextHiddenIds.add(source.context.id);
              }

              setHiddenIds(nextHiddenIds);
            }

            setA11yDragId(void 0);
          }
        }
      },
      orderBy: orderBy,
      renderNode: function renderNode(_ref5) {
        var context = _ref5.context,
            focused = _ref5.focused,
            node = _ref5.node;
        return /*#__PURE__*/jsx(Node, {
          a11yDragId: a11yDragId,
          canOrder: canOrder,
          canDrag: canDrag,
          canDrop: canDrop,
          context: context,
          focused: focused,
          node: node,
          onExpand: function onExpand() {
            var nextHiddenIds = new Set(hiddenIds);
            context.getChildIds().map(function (childId) {
              return nextHiddenIds.delete(childId);
            });
            setHiddenIds(nextHiddenIds);

            if (_onExpand) {
              _onExpand({
                context: context,
                node: node
              });
            }
          },
          onChange: onChange,
          onCollapse: function onCollapse() {
            var nextHiddenIds = new Set(hiddenIds);
            context.traverseDepthFirst(function (descendantId) {
              return nextHiddenIds.add(descendantId);
            });
            setHiddenIds(nextHiddenIds);

            if (_onCollapse) {
              _onCollapse({
                context: context,
                node: node
              });
            }
          },
          renderButtons: renderButtons,
          renderIcon: renderIcon,
          renderLabel: renderLabel,
          variant: variant
        });
      }
    })]
  });
};

Tree.propTypes = {
  "canDrag": _PropTypes.func,
  "canDrop": _PropTypes.func,
  "canOrder": _PropTypes.bool,
  "filterBy": _PropTypes.func,
  "getInitialHiddenIds": _PropTypes.func,
  "nodeById": _PropTypes.object.isRequired,
  "onChange": _PropTypes.func,
  "onCollapse": _PropTypes.func,
  "onExpand": _PropTypes.func,
  "orderBy": _PropTypes.func,
  "renderButtons": _PropTypes.func,
  "renderIcon": _PropTypes.func,
  "renderLabel": _PropTypes.func.isRequired,
  "variant": _PropTypes.oneOfType([_PropTypes.shape({
    isSelected: _PropTypes.func,
    onSelect: _PropTypes.func,
    type: _PropTypes.oneOf(["select"])
  }), _PropTypes.shape({
    onDefaultAction: _PropTypes.func,
    renderCheckbox: _PropTypes.func,
    type: _PropTypes.oneOf(["multiselect"])
  })])
};
Tree.displayName = "Tree";
var dragNodeZIndex = 100;
var dragType = "node";
var StyledDragNodeWrapper = styled.div.withConfig({
  displayName: "Tree__StyledDragNodeWrapper",
  componentId: "sc-1178m10-3"
})({
  position: "relative",
  width: 260
});
var StyledDragNode = styled.div.withConfig({
  displayName: "Tree__StyledDragNode",
  componentId: "sc-1178m10-4"
})(function (_ref6) {
  var theme = _ref6.theme;
  return {
    backgroundColor: theme.neutralColors.white,
    border: "1px solid ".concat(theme.neutralColors.silver),
    borderRadius: "2px",
    boxShadow: getDropShadowByElevation(1),
    display: "flex",
    lineHeight: lineHeight,
    position: "relative",
    zIndex: dragNodeZIndex
  };
});
var StyledDragNodeStacked = styled.div.withConfig({
  displayName: "Tree__StyledDragNodeStacked",
  componentId: "sc-1178m10-5"
})(function (_ref7) {
  var i = _ref7.i,
      theme = _ref7.theme;
  return {
    background: theme.neutralColors.white,
    borderRadius: "2px",
    border: "1px solid ".concat(theme.neutralColors.silver),
    boxShadow: getDropShadowByElevation(1),
    height: getSpacing(8),
    position: "absolute",
    top: 1 + (i + 1) * getSpacingValue(getSpacing(1)),
    width: "100%",
    transform: "scaleX(".concat(1 - (i + 1) * 0.05, ")"),
    zIndex: dragNodeZIndex - (i + 1)
  };
});

var DragNode = function DragNode(_ref8) {
  var renderLabel = _ref8.renderLabel;

  var _dnd$useDragLayer = dnd.useDragLayer(function (monitor) {
    return {
      currentOffset: monitor.getSourceClientOffset(),
      isDragging: monitor.isDragging(),
      item: monitor.getItem(),
      itemType: monitor.getItemType()
    };
  }),
      currentOffset = _dnd$useDragLayer.currentOffset,
      isDragging = _dnd$useDragLayer.isDragging,
      item = _dnd$useDragLayer.item,
      itemType = _dnd$useDragLayer.itemType;

  if (itemType === dragType && isDragging) {
    return /*#__PURE__*/jsx("div", {
      style: {
        position: "fixed",
        pointerEvents: "none",
        zIndex: dragNodeZIndex,
        left: 0,
        top: 0,
        width: "100%",
        height: "100%"
      },
      children: /*#__PURE__*/jsxs(StyledDragNodeWrapper, {
        style: {
          transform: "translate(".concat(currentOffset === null || currentOffset === void 0 ? void 0 : currentOffset.x, "px, ").concat(currentOffset === null || currentOffset === void 0 ? void 0 : currentOffset.y, "px)")
        },
        children: [/*#__PURE__*/jsxs(StyledDragNode, {
          children: [/*#__PURE__*/jsx(StyledDragHandle, {
            borderWidth: 1,
            dragging: true,
            children: /*#__PURE__*/jsx(IconDrag, {
              size: 16
            })
          }), /*#__PURE__*/jsx(StyledLabel, {
            children: renderLabel(item)
          })]
        }), item.context.hasChildren() && /*#__PURE__*/jsxs(Fragment, {
          children: [/*#__PURE__*/jsx(StyledDragNodeStacked, {
            i: 0
          }), /*#__PURE__*/jsx(StyledDragNodeStacked, {
            i: 1
          })]
        })]
      })
    });
  } else {
    return null;
  }
};

DragNode.propTypes = {
  "renderLabel": _PropTypes.func.isRequired
};
DragNode.displayName = "DragNode";
var StyledDropBar = styled.div.withConfig({
  displayName: "Tree__StyledDropBar",
  componentId: "sc-1178m10-6"
})(function (_ref9) {
  var isDragOver = _ref9.isDragOver,
      position = _ref9.position,
      theme = _ref9.theme;
  var padding = 4;
  return _objectSpread(_objectSpread({}, position === "top" ? {
    top: -padding - 1
  } : {
    bottom: -padding - 1
  }), {}, {
    opacity: isDragOver ? 1 : 0,
    padding: "".concat(padding, "px 0"),
    position: "absolute",
    width: 260,
    zIndex: 2,
    "&::after": {
      content: "''",
      borderBottom: "dashed ".concat(defaultBorderWidth, "px ").concat(theme.CTAColors.primaryVariant),
      display: "block",
      height: 0
    }
  });
});
var StyledNode = styled.div.withConfig({
  displayName: "Tree__StyledNode",
  componentId: "sc-1178m10-7"
})(function (_ref10) {
  var emphasized = _ref10.emphasized,
      isDragging = _ref10.isDragging,
      isDragOver = _ref10.isDragOver,
      selected = _ref10.selected,
      theme = _ref10.theme;
  return _objectSpread(_objectSpread({}, function () {
    var defaultPadding = "2px";

    if (isDragging) {
      return {
        backgroundColor: theme.neutralColors.pearl,
        padding: defaultPadding
      };
    } else if (isDragOver) {
      return {
        backgroundColor: rgba(theme.CTAColors.primaryVariant, 0.1),
        border: "dashed ".concat(defaultBorderWidth, "px ").concat(theme.CTAColors.primaryVariant),
        padding: 0
      };
    } else if (selected) {
      return {
        backgroundColor: theme.neutralColors.silver,
        borderLeft: "solid 4px ".concat(theme.neutralColors.charcoal),
        padding: defaultPadding
      };
    } else if (emphasized) {
      return {
        backgroundColor: theme.neutralColors.pearl,
        padding: defaultPadding
      };
    } else {
      return {
        padding: defaultPadding
      };
    }
  }()), {}, {
    borderRadius: 2,
    display: "flex",
    position: "relative"
  });
});

var Node = function Node(_ref11) {
  var a11yDragId = _ref11.a11yDragId,
      canOrder = _ref11.canOrder,
      _ref11$canDrag = _ref11.canDrag,
      _canDrag = _ref11$canDrag === void 0 ? function () {
    return true;
  } : _ref11$canDrag,
      _ref11$canDrop = _ref11.canDrop,
      _canDrop = _ref11$canDrop === void 0 ? function () {
    return true;
  } : _ref11$canDrop,
      context = _ref11.context,
      focused = _ref11.focused,
      initialHeight = _ref11.height,
      node = _ref11.node,
      onChange = _ref11.onChange,
      onCollapse = _ref11.onCollapse,
      onExpand = _ref11.onExpand,
      renderButtons = _ref11.renderButtons,
      renderIcon = _ref11.renderIcon,
      renderLabel = _ref11.renderLabel,
      variant = _ref11.variant;

  var ref = React.useRef(null);

  var _React$useState5 = React.useState(initialHeight || 0),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      height = _React$useState6[0],
      setHeight = _React$useState6[1];

  var _React$useState7 = React.useState(false),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      hovered = _React$useState8[0],
      setHovered = _React$useState8[1];

  React.useEffect(function () {
    var _ref$current;

    setHeight(((_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.clientHeight) || initialHeight || 0);
  }, [initialHeight]);
  var width = 32;
  var emphasized = focused || hovered;
  var selected = React.useMemo(function () {
    if ((variant === null || variant === void 0 ? void 0 : variant.type) === "select") {
      return variant.isSelected({
        context: context,
        node: node
      });
    } else {
      return false;
    }
  }, [context, node, variant]);

  var _dnd$useDrag = dnd.useDrag(function () {
    return {
      canDrag: function canDrag() {
        return !!onChange && !a11yDragId && _canDrag({
          context: context,
          node: node
        });
      },
      collect: function collect(monitor) {
        return {
          canDragNode: monitor.canDrag(),
          isDragging: monitor.isDragging()
        };
      },
      item: {
        context: context,
        node: node
      },
      type: dragType
    };
  }, [context]),
      _dnd$useDrag2 = _slicedToArray(_dnd$useDrag, 3),
      _dnd$useDrag2$ = _dnd$useDrag2[0],
      canDragNode = _dnd$useDrag2$.canDragNode,
      isDragging = _dnd$useDrag2$.isDragging,
      connectDragSource = _dnd$useDrag2[1],
      connectDragPreview = _dnd$useDrag2[2];

  var _dnd$useDrop = dnd.useDrop(function () {
    return {
      accept: dragType,
      canDrop: function canDrop(source) {
        return canDropNodeValid({
          canDrop: _canDrop,
          source: source,
          target: {
            context: context,
            node: node
          }
        });
      },
      drop: function drop(source, monitor) {
        if (monitor.didDrop()) {
          return;
        }

        onChange({
          position: "childof",
          source: {
            context: source.context,
            node: source.node
          },
          target: {
            context: context,
            node: node
          }
        });
      },
      collect: function collect(monitor) {
        return {
          canDropNode: monitor.canDrop(),
          isDragOverNode: monitor.isOver()
        };
      }
    };
  }, [context]),
      _dnd$useDrop2 = _slicedToArray(_dnd$useDrop, 2),
      _dnd$useDrop2$ = _dnd$useDrop2[0],
      canDropNode = _dnd$useDrop2$.canDropNode,
      isDragOverNode = _dnd$useDrop2$.isDragOverNode,
      connectDropTarget = _dnd$useDrop2[1];

  var _dnd$useDrop3 = dnd.useDrop(function () {
    return {
      accept: dragType,
      canDrop: function canDrop(source) {
        return !!canOrder && source.context.id !== context.id && source.context.getNextSiblingId() !== context.id && !context.ancestorIds.includes(source.context.id) && _canDrop(context.parentId ? {
          context: context.getParentContext(),
          node: context.getParentNode()
        } : void 0);
      },
      drop: function drop(source) {
        onChange({
          position: "before",
          source: {
            context: source.context,
            node: source.node
          },
          target: {
            context: context,
            node: node
          }
        });
      },
      collect: function collect(monitor) {
        return {
          canDropTopBar: monitor.canDrop(),
          isDragOverTopBar: monitor.isOver()
        };
      }
    };
  }, [context]),
      _dnd$useDrop4 = _slicedToArray(_dnd$useDrop3, 2),
      _dnd$useDrop4$ = _dnd$useDrop4[0],
      canDropTopBar = _dnd$useDrop4$.canDropTopBar,
      isDragOverTopBar = _dnd$useDrop4$.isDragOverTopBar,
      connectDropTopBarTarget = _dnd$useDrop4[1];

  var _dnd$useDrop5 = dnd.useDrop(function () {
    return {
      accept: dragType,
      canDrop: function canDrop(source) {
        return !!canOrder && source.context.id !== context.id && source.context.getPrevSiblingId() !== context.id && !context.ancestorIds.includes(source.context.id) && _canDrop(context.parentId ? {
          context: context.getParentContext(),
          node: context.getParentNode()
        } : void 0);
      },
      drop: function drop(source) {
        onChange({
          position: "after",
          source: {
            context: source.context,
            node: source.node
          },
          target: {
            context: context,
            node: node
          }
        });
      },
      collect: function collect(monitor) {
        return {
          canDropBottomBar: monitor.canDrop(),
          isDragOverBottomBar: monitor.isOver()
        };
      }
    };
  }, [context]),
      _dnd$useDrop6 = _slicedToArray(_dnd$useDrop5, 2),
      _dnd$useDrop6$ = _dnd$useDrop6[0],
      canDropBottomBar = _dnd$useDrop6$.canDropBottomBar,
      isDragOverBottomBar = _dnd$useDrop6$.isDragOverBottomBar,
      connectDropBottomBarTarget = _dnd$useDrop6[1];

  React.useEffect(function () {
    connectDragPreview(getEmptyImage(), {
      captureDraggingState: true
    });
  }, [connectDragPreview]);
  React.useEffect(function () {
    if (isDragOverNode && context.isCollapsed()) {
      onExpand();
    }
  }, [context, isDragOverNode, onExpand]);
  var isAnyDragging = isDragging || a11yDragId === context.id;
  var isA11yDragOverNode = !!a11yDragId && !!focused && canDropNodeValid({
    canDrop: _canDrop,
    source: {
      context: context.treeContext.getNodeContext(a11yDragId),
      node: context.treeContext.nodeById[a11yDragId]
    },
    target: {
      context: context,
      node: node
    }
  });
  return /*#__PURE__*/jsxs("div", {
    ref: ref,
    style: {
      display: "flex"
    },
    children: [/*#__PURE__*/jsxs("div", {
      style: {
        flex: "0",
        position: "relative"
      },
      children: [/*#__PURE__*/jsx(Descender, {
        hasChildren: context.hasChildren(),
        hasDescenderByLevel: context.hasDescenderByLevel,
        height: height,
        isLastChild: context.isLastChild(),
        stroke: "silver",
        width: width
      }), context.hasChildren() && height > 0 && /*#__PURE__*/jsx("div", {
        style: {
          left: context.hasDescenderByLevel.length * width - 2,
          position: "absolute",
          top: height / 2 - 11
        },
        children: context.isCollapsed() ? /*#__PURE__*/jsx(IconArrowRight, {
          onClick: onExpand,
          size: 16
        }) : /*#__PURE__*/jsx(IconArrowDown, {
          onClick: onCollapse,
          size: 16
        })
      })]
    }), /*#__PURE__*/jsx("div", {
      onMouseEnter: function onMouseEnter() {
        return setHovered(true);
      },
      onMouseLeave: function onMouseLeave() {
        return setHovered(false);
      },
      style: {
        flex: "1"
      },
      children: /*#__PURE__*/jsxs(StyledNode, _objectSpread(_objectSpread({
        emphasized: emphasized,
        isDragging: isAnyDragging,
        isDragOver: isDragOverNode && canDropNode && !(isDragOverBottomBar || isDragOverTopBar) || isA11yDragOverNode,
        ref: connectDropTarget
      }, (variant === null || variant === void 0 ? void 0 : variant.type) === "select" ? {
        selected: selected,
        onClick: function onClick() {
          return variant.onSelect({
            context: context,
            node: node
          });
        }
      } : void 0), {}, {
        children: [function () {
          if (isDragOverNode) {
            return /*#__PURE__*/jsxs(Fragment, {
              children: [/*#__PURE__*/jsx(StyledDropBar, {
                position: "bottom",
                isDragOver: isDragOverBottomBar && canDropBottomBar,
                ref: connectDropBottomBarTarget
              }), /*#__PURE__*/jsx(StyledDropBar, {
                position: "top",
                isDragOver: isDragOverTopBar && canDropTopBar,
                ref: connectDropTopBarTarget
              })]
            });
          }
        }(), onChange && hovered && /*#__PURE__*/jsx(StyledDragHandle, {
          disabled: !canDragNode,
          isDragPlaceholder: isAnyDragging,
          ref: connectDragSource,
          children: /*#__PURE__*/jsx(IconDrag, {
            size: 16
          })
        }), function () {
          if (!isAnyDragging && (variant === null || variant === void 0 ? void 0 : variant.type) === "multiselect") {
            var checkboxProps = variant.renderCheckbox({
              context: context,
              node: node
            });
            return /*#__PURE__*/jsx(StyledIconWrapper, {
              children: /*#__PURE__*/jsx(Checkbox, _objectSpread(_objectSpread({}, checkboxProps), {}, {
                "aria-label": "Select item",
                onFocus: function onFocus(event) {
                  return event.stopPropagation();
                },
                size: 16,
                tabIndex: focused && !checkboxProps.disabled ? 0 : -1
              }))
            });
          }
        }(), !isAnyDragging && renderIcon && /*#__PURE__*/jsx(StyledIconWrapper, {
          children: /*#__PURE__*/React.cloneElement(renderIcon({
            context: context,
            node: node
          }), {
            size: 16
          })
        }), /*#__PURE__*/jsx(StyledLabel, {
          isDragging: isAnyDragging,
          children: renderLabel({
            context: context,
            node: node
          })
        }), !isAnyDragging && renderButtons && (hovered || selected) && renderButtons({
          context: context,
          node: node
        }).map(function (_ref12, i) {
          var buttonProps = _ref12.buttonProps,
              tooltipProps = _ref12.tooltipProps;

          var buttonEl = /*#__PURE__*/createElement(Button, _objectSpread(_objectSpread({}, buttonProps), {}, {
            hideLabel: true,
            key: i,
            label: "",
            onFocus: function onFocus(event) {
              return event.stopPropagation();
            },
            onKeyDown: function onKeyDown(event) {
              return event.stopPropagation();
            },
            variant: "ghost"
          }));

          if (!tooltipProps) {
            return buttonEl;
          } else {
            return /*#__PURE__*/createElement(Tooltip, _objectSpread(_objectSpread({}, tooltipProps), {}, {
              key: i
            }), buttonEl);
          }
        })]
      }))
    })]
  });
};

Node.propTypes = {
  "a11yDragId": _PropTypes.string,
  "canDrag": _PropTypes.func,
  "canDrop": _PropTypes.func,
  "canOrder": _PropTypes.bool,
  "context": _PropTypes.shape({
    "absoluteIndex": _PropTypes.number.isRequired,
    "ancestorIds": _PropTypes.arrayOf(_PropTypes.string).isRequired,
    "getAriaExpanded": _PropTypes.func.isRequired,
    "getAriaLevel": _PropTypes.func.isRequired,
    "getAriaPosInSet": _PropTypes.func.isRequired,
    "getAriaRowIndex": _PropTypes.func.isRequired,
    "getAriaSetSize": _PropTypes.func.isRequired,
    "getChildIds": _PropTypes.func.isRequired,
    "getNextSiblingId": _PropTypes.func.isRequired,
    "getParentContext": _PropTypes.func.isRequired,
    "getParentNode": _PropTypes.func.isRequired,
    "getPrevSiblingId": _PropTypes.func.isRequired,
    "getSiblingsAndSelf": _PropTypes.func.isRequired,
    "hasChildren": _PropTypes.func.isRequired,
    "hasDescenderByLevel": _PropTypes.arrayOf(_PropTypes.bool).isRequired,
    "id": _PropTypes.string.isRequired,
    "isCollapsed": _PropTypes.func.isRequired,
    "isExpanded": _PropTypes.func.isRequired,
    "isLastChild": _PropTypes.func.isRequired,
    "parentId": _PropTypes.string,
    "traverseDepthFirst": _PropTypes.func.isRequired,
    "treeContext": _PropTypes.shape({
      "childIdsByParentId": _PropTypes.object.isRequired,
      "getLastVisibleIndex": _PropTypes.func.isRequired,
      "getNextVisibleAbsoluteIndex": _PropTypes.func.isRequired,
      "getNextVisibleIndexByIndex": _PropTypes.func.isRequired,
      "getNodeContext": _PropTypes.func.isRequired,
      "getNodeCount": _PropTypes.func.isRequired,
      "getPrevVisibleAbsoluteIndex": _PropTypes.func.isRequired,
      "getPrevVisibleIndexByIndex": _PropTypes.func.isRequired,
      "hiddenIds": _PropTypes.object.isRequired,
      "lastVisibleIndex": _PropTypes.number,
      "nextVisibleIndexByIndex": _PropTypes.object,
      "nodeById": _PropTypes.object.isRequired,
      "nodeContextById": _PropTypes.object.isRequired,
      "nodeCount": _PropTypes.number,
      "nodeIds": _PropTypes.arrayOf(_PropTypes.string).isRequired,
      "nodes": _PropTypes.arrayOf(_PropTypes.shape({
        "parentId": _PropTypes.string
      })).isRequired,
      "prevVisibleIndexByIndex": _PropTypes.object,
      "rootIds": _PropTypes.arrayOf(_PropTypes.string).isRequired,
      "traverseDepthFirst": _PropTypes.func.isRequired
    }).isRequired
  }).isRequired,
  "focused": _PropTypes.bool,
  "height": _PropTypes.number,
  "node": _PropTypes.shape({
    "parentId": _PropTypes.string
  }).isRequired,
  "onChange": _PropTypes.func,
  "onCollapse": _PropTypes.func.isRequired,
  "onExpand": _PropTypes.func.isRequired,
  "renderButtons": _PropTypes.func,
  "renderIcon": _PropTypes.func,
  "renderLabel": _PropTypes.func.isRequired,
  "variant": _PropTypes.oneOfType([_PropTypes.shape({
    isSelected: _PropTypes.func,
    onSelect: _PropTypes.func,
    type: _PropTypes.oneOf(["select"])
  }), _PropTypes.shape({
    onDefaultAction: _PropTypes.func,
    renderCheckbox: _PropTypes.func,
    type: _PropTypes.oneOf(["multiselect"])
  })])
};
Node.displayName = "Node";

function canDropNodeValid(_ref13) {
  var canDrop = _ref13.canDrop,
      source = _ref13.source,
      target = _ref13.target;
  return source.context.id !== target.context.id && source.context.parentId !== target.context.id && !target.context.ancestorIds.includes(source.context.id) && canDrop(target);
}

function getSpacingValue(spacing) {
  return parseInt(typeof spacing === "string" ? spacing : spacing1x, 10);
}

export { Tree, dnd };
